import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Shield, 
  Send, 
  AlertTriangle, 
  CheckCircle2, 
  Lock,
  FileText,
  Calendar,
  Users,
  Info
} from 'lucide-react';
import api from '../lib/api';

const TIPO_SEGNALAZIONE_OPTIONS = [
  { value: 'molestia', label: 'Molestia' },
  { value: 'discriminazione', label: 'Discriminazione' },
  { value: 'comportamento_scorretto', label: 'Comportamento scorretto' },
  { value: 'altro', label: 'Altro' }
];

export const SegnalazioniPage = () => {
  const [formData, setFormData] = useState({
    tipo_segnalazione: '',
    descrizione: '',
    data_episodio: '',
    testimoni: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipo_segnalazione || !formData.descrizione.trim()) {
      toast.error('Compila i campi obbligatori', {
        description: 'Tipo di segnalazione e descrizione sono richiesti'
      });
      return;
    }

    if (formData.descrizione.trim().length < 20) {
      toast.error('Descrizione troppo breve', {
        description: 'Fornisci una descrizione più dettagliata (almeno 20 caratteri)'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/segnalazioni', {
        tipo_segnalazione: formData.tipo_segnalazione,
        descrizione: formData.descrizione.trim(),
        data_episodio: formData.data_episodio || null,
        testimoni: formData.testimoni.trim() || null
      });
      
      setIsSubmitted(true);
      toast.success('Segnalazione inviata', {
        description: 'La tua segnalazione è stata inviata in modo completamente anonimo'
      });
    } catch (error) {
      console.error('Error submitting segnalazione:', error);
      toast.error('Errore nell\'invio', {
        description: error.response?.data?.detail || 'Si è verificato un errore. Riprova più tardi.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewReport = () => {
    setFormData({
      tipo_segnalazione: '',
      descrizione: '',
      data_episodio: '',
      testimoni: ''
    });
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="segnalazioni-success">
        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                Segnalazione Inviata
              </h2>
              <p className="text-emerald-700 dark:text-emerald-300">
                La tua segnalazione è stata inviata in modo <strong>completamente anonimo</strong>.
              </p>
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                Non è possibile risalire alla tua identità. Grazie per aver contribuito 
                a mantenere un ambiente di lavoro sicuro e rispettoso.
              </p>
              <Button 
                onClick={handleNewReport}
                variant="outline"
                className="mt-4"
                data-testid="new-report-btn"
              >
                Invia un'altra segnalazione
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="segnalazioni-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
            <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Parità di Genere</h1>
            <p className="text-sm text-muted-foreground">Modulo di Segnalazione Anonima</p>
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
        <Lock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <strong>Segnalazione 100% Anonima</strong> - Questo modulo non raccoglie alcun dato 
          personale identificativo. La tua segnalazione sarà completamente anonima e non 
          sarà possibile risalire alla tua identità.
        </AlertDescription>
      </Alert>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Modulo di Segnalazione
          </CardTitle>
          <CardDescription>
            Utilizza questo modulo per segnalare comportamenti scorretti, molestie o 
            discriminazioni sul luogo di lavoro. La tua segnalazione sarà trattata 
            con la massima riservatezza.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo Segnalazione */}
            <div className="space-y-2">
              <Label htmlFor="tipo_segnalazione" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Tipo di Segnalazione <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.tipo_segnalazione} 
                onValueChange={(value) => handleChange('tipo_segnalazione', value)}
              >
                <SelectTrigger data-testid="tipo-segnalazione-select">
                  <SelectValue placeholder="Seleziona il tipo di segnalazione" />
                </SelectTrigger>
                <SelectContent>
                  {TIPO_SEGNALAZIONE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label htmlFor="descrizione" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                Descrizione Dettagliata <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="descrizione"
                placeholder="Descrivi in modo dettagliato l'episodio o il comportamento che vuoi segnalare. Includi tutte le informazioni rilevanti che ritieni utili..."
                value={formData.descrizione}
                onChange={(e) => handleChange('descrizione', e.target.value)}
                rows={6}
                className="resize-none"
                data-testid="descrizione-input"
              />
              <p className="text-xs text-muted-foreground">
                Minimo 20 caratteri. Sii il più dettagliato possibile.
              </p>
            </div>

            {/* Data Episodio (Opzionale) */}
            <div className="space-y-2">
              <Label htmlFor="data_episodio" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                Data/Periodo dell'Episodio <span className="text-muted-foreground text-xs">(opzionale)</span>
              </Label>
              <Input
                id="data_episodio"
                type="text"
                placeholder="Es: 15 gennaio 2026, oppure 'settimana scorsa', 'ultimo mese'..."
                value={formData.data_episodio}
                onChange={(e) => handleChange('data_episodio', e.target.value)}
                data-testid="data-episodio-input"
              />
            </div>

            {/* Testimoni (Opzionale) */}
            <div className="space-y-2">
              <Label htmlFor="testimoni" className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" />
                Eventuali Testimoni <span className="text-muted-foreground text-xs">(opzionale)</span>
              </Label>
              <Textarea
                id="testimoni"
                placeholder="Se ci sono stati testimoni, puoi indicarlo qui (senza necessariamente fornire nomi)..."
                value={formData.testimoni}
                onChange={(e) => handleChange('testimoni', e.target.value)}
                rows={2}
                className="resize-none"
                data-testid="testimoni-input"
              />
            </div>

            {/* Info Box */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                La segnalazione verrà inviata alla casella <strong>info@agriisland.it</strong> in 
                modo completamente anonimo. Non verranno raccolti dati identificativi come 
                IP, nome utente o email.
              </AlertDescription>
            </Alert>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={isSubmitting}
              data-testid="submit-segnalazione-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Invia Segnalazione Anonima
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Footer Note */}
      <p className="text-xs text-center text-muted-foreground">
        Questo modulo è parte del programma aziendale per la Parità di Genere (UNI/PdR 125:2022).
        <br />
        Tutte le segnalazioni vengono gestite nel rispetto della normativa vigente.
      </p>
    </div>
  );
};

export default SegnalazioniPage;
